import fuzzysort from "fuzzysort";

/** One ranked hit: the original item, plus the matched-character offsets into
 *  its target string (for highlighting). An empty query yields every item in
 *  original order with `indexes: []` (nothing to highlight). */
export interface FuzzyMatch<T> {
  item: T;
  target: string;
  /** 1 = perfect, 0.5 = good, higher is better. 0 for the empty-query passthrough. */
  score: number;
  /** Char offsets in `target` that matched the needle, ascending. */
  indexes: readonly number[];
}

/**
 * Shared client-side fuzzy matcher (thin wrapper over `fuzzysort`): ranked
 * results plus matched-character ranges, so callers can filter *and* highlight
 * from one pass instead of ad-hoc `includes()`/`toLowerCase()`.
 *
 * `key` maps an item to the string to match against. An empty/blank query is a
 * passthrough — every item, original order, no highlight ranges — so a picker
 * can render its full list before the user types. `threshold` (0–1, higher is
 * stricter; default 0 = keep any subsequence match) drops weak scattered hits.
 */
export function fuzzyRank<T>(
  query: string,
  items: readonly T[],
  key: (item: T) => string,
  opts: { limit?: number; threshold?: number } = {},
): FuzzyMatch<T>[] {
  const q = query.trim();
  if (!q) {
    const all = opts.limit != null ? items.slice(0, opts.limit) : items;
    return all.map((item) => ({ item, target: key(item), score: 0, indexes: [] }));
  }
  const results = fuzzysort.go(q, items, {
    key,
    limit: opts.limit,
    threshold: opts.threshold ?? 0,
  });
  return results.map((r) => ({
    item: r.obj,
    target: r.target,
    score: r.score,
    indexes: r.indexes,
  }));
}

/** Split `text` into consecutive segments, each flagged as a match (`hit`) or
 *  not, from a set of matched offsets. Renderers wrap the `hit` runs (e.g. in
 *  `<mark>`). Offsets outside `[0, text.length)` are ignored, so a caller can
 *  pre-shift indexes to highlight a substring (e.g. a path's basename). */
export function matchSegments(
  text: string,
  indexes: readonly number[],
): { text: string; hit: boolean }[] {
  const hits = new Set(indexes.filter((i) => i >= 0 && i < text.length));
  if (hits.size === 0) return [{ text, hit: false }];
  const out: { text: string; hit: boolean }[] = [];
  let run = "";
  let runHit = hits.has(0);
  for (let i = 0; i < text.length; i++) {
    const h = hits.has(i);
    if (h === runHit) {
      run += text[i];
    } else {
      out.push({ text: run, hit: runHit });
      run = text[i];
      runHit = h;
    }
  }
  out.push({ text: run, hit: runHit });
  return out;
}
